import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, AddressElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Lock, CheckCircle2, AlertCircle, ShoppingCart, ArrowLeft } from 'lucide-react';
import PageLayout from '../../components/Layout/PageLayout';
import { useCart } from '../../context/CartContext';
import './CheckoutPage.scss';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// Payment form — must be rendered inside <Elements>
const PaymentForm = ({ total, onSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const { clearCart } = useCart();
    const [form, setForm] = useState({ name: '', email: '', phone: '', company: '' });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        if (!stripe || !elements) return;
        if (!form.name || !form.email) { setError('Name and email are required.'); return; }

        // Shipping address comes from Stripe's AddressElement — must be complete
        // before we charge, since it's what the order actually ships to.
        const addressEl = elements.getElement('address');
        let shipping = null;
        if (addressEl) {
            const { complete, value } = await addressEl.getValue();
            if (!complete) {
                setError('Please complete the shipping address.');
                return;
            }
            shipping = { name: value.name || form.name, phone: value.phone || form.phone, address: value.address };
        }

        setSubmitting(true);
        const { error: err, paymentIntent } = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
            confirmParams: {
                receipt_email: form.email,
                ...(shipping ? { shipping } : {}),
                // We supply name/email/phone from the Contact fields above (they're
                // set to 'never' on PaymentElement); Stripe collects the billing
                // ADDRESS itself inside the card block.
                payment_method_data: { billing_details: { name: form.name, email: form.email, phone: form.phone } },
            },
        });

        if (err) {
            setError(err.message || 'Payment failed. Please check your card details.');
            setSubmitting(false);
            return;
        }

        if (paymentIntent && paymentIntent.status === 'succeeded') {
            let confirmedTotal = total;
            try {
                const res = await fetch(`${API_BASE_URL}/payment/confirm`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ paymentIntentId: paymentIntent.id, customer: form, shipping }),
                });
                const data = await res.json().catch(() => ({}));
                if (data && data.total) confirmedTotal = data.total;
            } catch (e2) { /* payment already succeeded; ignore confirm/email errors */ }
            clearCart();
            onSuccess({ transactionId: paymentIntent.id, total: confirmedTotal });
        } else {
            setError('Payment could not be completed. Please try again.');
            setSubmitting(false);
        }
    };

    return (
        <form className="jk-checkout__form" onSubmit={submit}>
            {error && (
                <div className="jk-checkout__alert"><AlertCircle size={18} /> <span>{error}</span></div>
            )}
            <h2>Contact</h2>
            <div className="jk-checkout__row">
                <div className="jk-checkout__field">
                    <label>Full Name *</label>
                    <input name="name" value={form.name} onChange={change} required />
                </div>
                <div className="jk-checkout__field">
                    <label>Email *</label>
                    <input type="email" name="email" value={form.email} onChange={change} required />
                </div>
            </div>
            <div className="jk-checkout__row">
                <div className="jk-checkout__field">
                    <label>Phone</label>
                    <input name="phone" value={form.phone} onChange={change} />
                </div>
                <div className="jk-checkout__field">
                    <label>Company</label>
                    <input name="company" value={form.company} onChange={change} />
                </div>
            </div>

            <h2 className="jk-checkout__card-heading">Shipping Address</h2>
            <div className="jk-checkout__pe">
                <AddressElement
                    options={{
                        mode: 'shipping',
                        allowedCountries: ['US'],
                        fields: { phone: 'always' },
                        defaultValues: { name: form.name, phone: form.phone },
                    }}
                />
            </div>

            <h2 className="jk-checkout__card-heading">Payment</h2>
            <div className="jk-checkout__pe">
                {/* name/email/phone come from the Contact fields above, so tell
                    Stripe never to ask for them again; it still collects the
                    billing address inside the card block. */}
                <PaymentElement
                    options={{
                        fields: {
                            billingDetails: {
                                name: 'never',
                                email: 'never',
                                phone: 'never',
                                address: 'auto',
                            },
                        },
                    }}
                />
            </div>

            <button type="submit" className="jk-btn jk-btn--primary jk-btn--full jk-checkout__pay" disabled={!stripe || submitting}>
                <Lock size={18} /> {submitting ? 'Processing…' : `Pay ${money(total)}`}
            </button>
            <p className="jk-checkout__secure"><Lock size={13} /> Secured by Stripe — your card details never touch our servers.</p>
        </form>
    );
};

const CheckoutPage = () => {
    const { items } = useCart();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [clientSecret, setClientSecret] = useState('');
    const [payable, setPayable] = useState([]);
    const [quoteOnly, setQuoteOnly] = useState([]);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!items || items.length === 0) { setLoading(false); return; }
            if (!stripePromise) {
                setError('Payments are not configured yet. Please contact us to complete your order.');
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const res = await fetch(`${API_BASE_URL}/payment/create-intent`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items: items.map((i) => ({ id: i.id, name: i.name, quantity: i.quantity })) }),
                });
                const data = await res.json().catch(() => ({}));
                if (cancelled) return;
                if (!res.ok || !data.success) {
                    setError(data.error || 'Could not start checkout.');
                } else {
                    setClientSecret(data.clientSecret);
                    setPayable(data.items || []);
                    setTotal(data.total || 0);
                    const paidIds = new Set((data.items || []).map((p) => p.id));
                    setQuoteOnly(items.filter((i) => !paidIds.has(i.id)));
                }
            } catch (e) {
                if (!cancelled) setError('Could not start checkout. Please try again.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [items]);

    // On success/empty-cart the view swaps in place — jump to the top so the
    // user sees the confirmation card, not wherever they were scrolled (footer).
    useEffect(() => {
        if (success) window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [success]);

    // --- Success ---
    if (success) {
        return (
            <PageLayout solidHeader>
                <div className="jk-checkout jk-checkout--center">
                    <div className="jk-checkout__result">
                        <CheckCircle2 size={56} className="jk-checkout__result-icon jk-checkout__result-icon--ok" />
                        <h1>Payment Successful</h1>
                        <p>Thank you! Your order has been placed and paid.</p>
                        <p className="jk-checkout__result-meta">
                            Reference: <strong>{success.transactionId}</strong><br />
                            Amount charged: <strong>{money(success.total)}</strong>
                        </p>
                        <Link to="/accessories" className="jk-btn jk-btn--primary">Continue Shopping</Link>
                    </div>
                </div>
            </PageLayout>
        );
    }

    // --- Empty cart ---
    if (!loading && (!items || items.length === 0)) {
        return (
            <PageLayout solidHeader>
                <div className="jk-checkout jk-checkout--center">
                    <div className="jk-checkout__result">
                        <ShoppingCart size={56} className="jk-checkout__result-icon" />
                        <h1>Your cart is empty</h1>
                        <p>Add some products before checking out.</p>
                        <Link to="/accessories" className="jk-btn jk-btn--primary">Browse Products</Link>
                    </div>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout solidHeader>
            <div className="jk-checkout">
                <div className="jk-checkout__container">
                    <button className="jk-checkout__back" onClick={() => navigate('/cart')}>
                        <ArrowLeft size={18} /> Back to Cart
                    </button>
                    <h1 className="jk-checkout__title">Checkout</h1>

                    {error && (
                        <div className="jk-checkout__alert"><AlertCircle size={18} /> <span>{error}</span></div>
                    )}

                    <div className="jk-checkout__grid">
                        <div>
                            {loading ? (
                                <p className="jk-checkout__muted">Preparing secure checkout…</p>
                            ) : clientSecret ? (
                                <Elements
                                    stripe={stripePromise}
                                    options={{ clientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: '#2563eb' } } }}
                                >
                                    <PaymentForm total={total} onSuccess={setSuccess} />
                                </Elements>
                            ) : (
                                !error && <p className="jk-checkout__muted">No purchasable items in your cart.</p>
                            )}
                        </div>

                        <aside className="jk-checkout__summary">
                            <h2>Order Summary</h2>
                            {loading ? (
                                <p className="jk-checkout__muted">Verifying live prices…</p>
                            ) : (
                                <>
                                    {payable.map((it) => (
                                        <div key={it.id} className="jk-checkout__line">
                                            <span>{it.name} <em>×{it.quantity}</em></span>
                                            <span>{money(it.unitPrice * it.quantity)}</span>
                                        </div>
                                    ))}
                                    {quoteOnly.length > 0 && (
                                        <div className="jk-checkout__quoteonly">
                                            <p>Quote-only (no live price — not charged):</p>
                                            {quoteOnly.map((it) => (
                                                <div key={it.id} className="jk-checkout__line jk-checkout__line--muted">
                                                    <span>{it.name} <em>×{it.quantity || 1}</em></span>
                                                    <span>Quote</span>
                                                </div>
                                            ))}
                                            <Link to="/cart" className="jk-checkout__quotelink">Request a quote for these →</Link>
                                        </div>
                                    )}
                                    <div className="jk-checkout__total">
                                        <span>Total</span>
                                        <span>{money(total)}</span>
                                    </div>
                                </>
                            )}
                        </aside>
                    </div>
                </div>
            </div>
        </PageLayout>
    );
};

export default CheckoutPage;
